import { useRouter } from "@tanstack/react-router";
import { createContext, useContext, useEffect, useId, useRef, type ReactNode } from "react";

/**
 * Browser Back closes an open overlay instead of leaving the page.
 *
 * Opening a dialog pushes a history entry (same URL, marker in `history.state`); popping that
 * entry closes the dialog, and closing it from the UI pops the entry we pushed so the stack
 * stays clean. Overlays whose open state already lives in the URL (the composer's
 * `?delivery=true` takeover, `/home?sent=…`) must NOT use this — Back pops their search
 * param instead — so it is opt-in: `<BackClosesOverlays>` turns it on for every `Dialog` /
 * `Sheet` underneath (the card create flow), and the `backCloses` prop overrides per dialog.
 *
 * The marker rides on the router's own history state (`__TSR_index` + 1) so TanStack sees
 * a regular BACK when it pops, and the route re-parses to the same location.
 */
const BackClosesContext = createContext(false);

export function BackClosesOverlays({ children }: { children: ReactNode }) {
  return <BackClosesContext.Provider value={true}>{children}</BackClosesContext.Provider>;
}

const MARKER = "__overlay";

function currentMarker(): string | undefined {
  const state: unknown = window.history.state;
  if (state && typeof state === "object" && MARKER in state) {
    const v = (state as Record<string, unknown>)[MARKER];
    return typeof v === "string" ? v : undefined;
  }
  return undefined;
}

// A close + open in the same commit (benefits table → paywall) would otherwise queue a
// history.back() and then push on top of it, and the back would pop the new dialog. So a
// close only SCHEDULES its back (microtask); an open landing in the same commit claims the
// pending entry with replaceState instead of pushing another. The microtask re-checks the
// marker before popping: a navigation made in the same tick as the close (Leave → /cards)
// flushes its own pushState first, and popping THAT would bounce the member back.
let pendingBack: string | null = null;
/** A history.back() we issued that the browser hasn't finished traversing yet. */
let backInFlight = false;
let settleWaiters: (() => void)[] = [];

function flushSettled() {
  const waiters = settleWaiters;
  settleWaiters = [];
  for (const w of waiters) w();
}

function scheduleBack(id: string) {
  if (pendingBack !== null) return;
  pendingBack = id;
  queueMicrotask(() => {
    if (pendingBack !== id) return;
    pendingBack = null;
    if (currentMarker() !== id) return flushSettled();
    // history.back() is asynchronous: a pushState made before its popstate lands gets
    // traversed back over. Track it so navigations can wait (see afterOverlayHistory).
    backInFlight = true;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      backInFlight = false;
      window.removeEventListener("popstate", finish);
      flushSettled();
    };
    window.addEventListener("popstate", finish);
    setTimeout(finish, 400); // a traversal the browser drops never fires popstate
    window.history.back();
  });
}

/**
 * Run `fn` once any overlay's Back-entry pop has finished. A navigation made while a
 * closing dialog's history.back() is still pending/in flight is undone by that traversal
 * — that is how sign-in's "open Delivery" replay kept vanishing as the authwall closed
 * (BDAY-201). Waits one task first, so a close committed in the same render (whose
 * cleanup schedules the back) is seen.
 */
export function afterOverlayHistory(fn: () => void): void {
  setTimeout(() => {
    if (pendingBack !== null || backInFlight) settleWaiters.push(fn);
    else fn();
  }, 0);
}

function claimEntry(id: string) {
  const prev: unknown = window.history.state;
  const base = prev && typeof prev === "object" ? (prev as Record<string, unknown>) : {};
  if (pendingBack !== null && currentMarker() === pendingBack) {
    pendingBack = null;
    window.history.replaceState({ ...base, [MARKER]: id }, "");
    return;
  }
  const index = typeof base.__TSR_index === "number" ? base.__TSR_index + 1 : undefined;
  window.history.pushState(
    { ...base, ...(index !== undefined ? { __TSR_index: index } : {}), [MARKER]: id },
    "",
  );
}

/**
 * Wire an overlay's `open` state to the history stack. `close` is called when Back pops the
 * entry pushed for this overlay; `enabled` undefined = inherit from `<BackClosesOverlays>`.
 */
export function useBackCloses(enabled: boolean | undefined, open: boolean, close: () => void) {
  const inherited = useContext(BackClosesContext);
  const active = (enabled ?? inherited) && open;
  const id = useId();
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | undefined;
  const closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  }, [close]);
  useEffect(() => {
    if (!active) return;
    // The router batches its own pushState in a microtask — land that first so our entry
    // sits ABOVE a navigation made in the same tick, not below it.
    router?.history.flush();
    claimEntry(id);
    const onPop = () => {
      // Our entry is gone (Back) — close. Forward back onto it is left alone: the dialog
      // is closed, and the next Back is a no-op on the same URL.
      if (currentMarker() !== id) closeRef.current();
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      // Closed from the UI while still on our entry — pop it. Closed because Back already
      // popped it, or because a navigation replaced/pushed over it, there's nothing to pop.
      if (currentMarker() === id) scheduleBack(id);
    };
  }, [active, id, router]);
}
